"use client"

import { ArrowRight, MessageCircle, Lightbulb, Sparkles } from "lucide-react"
import { Button } from "@/components/ui/button"
import { useInView } from "@/hooks/use-in-view"

export default function Process() {
  const [titleRef, titleInView] = useInView({ threshold: 0.3 })
  const [gridRef, gridInView] = useInView({ threshold: 0.2 })
  const [ctaRef, ctaInView] = useInView({ threshold: 0.3 })

  const processSteps = [
    {
      number: "1",
      title: "Connect",
      description: "Wir hören zu. Persönlich. Verbindlich.",
      icon: MessageCircle,
      delay: "delay-100"
    },
    {
      number: "2",
      title: "Concept",
      description: "Wir entwerfen ein stimmiges Konzept für euren Anlass.",
      icon: Lightbulb,
      delay: "delay-200"
    },
    {
      number: "3",
      title: "Create",
      description: "Wir liefern, kochen, dekorieren. Ihr genießt das Ergebnis.",
      icon: Sparkles,
      delay: "delay-300"
    }
  ]

  return (
    <section className="py-32 md:py-40 text-white relative overflow-hidden" aria-label="Anfrage & Ablauf">
      {/* Background pattern to match other sections */}
      <div className="absolute inset-0 bg-[linear-gradient(45deg,#111111_25%,transparent_25%,transparent_75%,#111111_75%,#111111),linear-gradient(45deg,#111111_25%,transparent_25%,transparent_75%,#111111_75%,#111111)] bg-[length:60px_60px] opacity-5 z-0"></div>

      <div className="container relative z-10">
        {/* Title Section */}
        <div
          ref={titleRef}
          className={`text-center mb-16 animate-on-scroll ${titleInView ? 'animate-fade-in-up' : ''}`}
        >
          <h2 className={`text-[3rem] md:text-[4rem] leading-none font-adobe italic tracking-wider uppercase`}>
            Anfrage & Ablauf
          </h2>
          <div className={`w-20 h-px bg-[#a19b92] my-6 mx-auto animate-on-scroll ${titleInView ? 'animate-slide-up-line delay-300' : ''}`}></div>
          <p className={`text-[#a19b92] max-w-2xl mx-auto leading-relaxed animate-on-scroll ${titleInView ? 'animate-fade-in-up delay-400' : ''}`}>
            Let's create something special – gemeinsam.
          </p>
        </div>

        {/* Process Steps */}
        <div
          ref={gridRef}
          className="max-w-5xl mx-auto mb-16"
        >
          <div className="grid md:grid-cols-3 gap-8 md:gap-12">
            {processSteps.map((step, index) => {
              const Icon = step.icon
              return (
                <div
                  key={step.number}
                  className={`group relative text-center animate-on-scroll ${gridInView ? `animate-fade-in-up ${step.delay}` : ''}`}
                >
                  <div className="relative p-8 md:p-10 border border-white/10 bg-white/5 backdrop-blur-md shadow-xl rounded-sm transition-all duration-500 hover:bg-white/10 hover:backdrop-blur-xl hover:shadow-2xl overflow-hidden h-full">
                    {/* Background gradient overlay */}
                    <div className="pointer-events-none absolute inset-0 z-0 bg-gradient-to-br from-[#a19b92]/20 via-[#222]/10 to-transparent opacity-60 group-hover:opacity-90 transition-opacity duration-300" />

                    <div className="relative z-10">
                      <div className="flex items-center justify-center mb-6">
                        <div className="relative">
                          <Icon className="w-7 h-7 text-white stroke-1" />
                          <div className="absolute -top-4 -right-4 w-5 h-5 bg-white text-black rounded-full flex items-center justify-center font-bold text-xs">
                            {step.number}
                          </div>
                        </div>
                      </div>

                      <h3 className="text-xl md:text-2xl uppercase tracking-wider text-white mb-4">
                        {step.title}
                      </h3>

                      <p className="text-[#a19b92] leading-relaxed text-sm md:text-base">
                        {step.description}
                      </p>
                    </div>

                    {/* Connector arrow for desktop */}
                    {index < processSteps.length - 1 && (
                      <div className="hidden md:block absolute top-1/2 -right-6 transform -translate-y-1/2 z-20">
                        <ArrowRight className="w-5 h-5 text-[#a19b92]" />
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* CTA Section */}
        <div
          ref={ctaRef}
          className={`text-center animate-on-scroll ${ctaInView ? 'animate-fade-in-up delay-200' : ''}`}
        >
          <a
            href="#contact"
            className="bg-white text-black px-8 py-3 uppercase tracking-wider font-medium hover:bg-white/90 transition-colors duration-300"
          >
            Jetzt anfragen
          </a>
        </div>
      </div>
    </section>
  )
}